import React, { useMemo, useReducer } from 'react';
import { TourProvider } from '@shared/components/Tour';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AccommodationsTourProviderProps, AccommodationsTourStepType, AccommodationsTourState, AccommodationsTourAction } from './AccommodationsTour.types';
import { STEP_ORDER } from './AccommodationsTour.types';
import { useAccommodationsTourStepContext } from './AccommodationsTourStepContext';
import { AccommodationsTourStepProvider } from './AccommodationsTourStepContext';
import { useUserConsent } from '@shared/utils/userConsent/useUserConsent';
import { addAction } from '@shared/utils/logger';

const initialState: AccommodationsTourState = {
  steps: { hotel: [] as const, guest: [] as const } as const,
} as const;

const reducer = (state: AccommodationsTourState, action: AccommodationsTourAction): AccommodationsTourState => {
  const { stepType, stepId, skipped } = action;
  const currentSteps = state.steps[stepType];
  const existingStepIndex = currentSteps.findIndex(step => step.stepId === stepId);

  // Update existing step if it exists, otherwise add new step
  if (existingStepIndex >= 0) {
    const updatedSteps = [...currentSteps];
    updatedSteps[existingStepIndex] = { stepId, stepType, skipped };
    return {
      steps: {
        ...state.steps,
        [stepType]: updatedSteps,
      },
    };
  }

  return {
    steps: {
      ...state.steps,
      [stepType]: [...currentSteps, { stepId, stepType, skipped }],
    },
  };
};

/**
 * Internal component that builds the tour from registered steps
 */
const AccommodationsTourProviderInner: React.FC<AccommodationsTourProviderProps> = ({ children }) => {
  const { registeredSteps, tourName } = useAccommodationsTourStepContext();
  const { setUserConsent } = useUserConsent();

  // Flatten registered steps into ordered array: venue → hotel → guest, filtering out disabled steps
  const stepOrder = useMemo(() => {
    const enabledSteps = STEP_ORDER.flatMap(type => registeredSteps[type].filter(step => !step.skipped).map(step => step.stepId));

    // Log skipped steps for debugging/analytics
    const skippedSteps = STEP_ORDER.flatMap(type => registeredSteps[type].filter(step => step.skipped).map(step => ({ stepType: step.stepType, stepId: step.stepId })));

    if (skippedSteps.length > 0) {
      addAction('AccommodationsTourStepsSkipped', {
        skippedCount: skippedSteps.length,
        skippedSteps: skippedSteps.map(s => `${s.stepType}:${s.stepId}`).join(','),
      });
    }

    return enabledSteps;
  }, [registeredSteps]);

  const handleComplete = useEventCallback(() => {
    setUserConsent('hasSeenAccommodationsTour', true);
  });

  return (
    <TourProvider
      tourName={tourName}
      stepOrder={stepOrder}
      onComplete={handleComplete}
    >
      {children}
    </TourProvider>
  );
};

AccommodationsTourProviderInner.displayName = 'AccommodationsTourProviderInner';

/**
 * Provider for accommodations tour with dynamic step registration.
 * Steps self-register on mount and are ordered by type: venue → hotel → guest.
 */
export const AccommodationsTourProvider: React.FC<AccommodationsTourProviderProps> = props => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const registerStep = useEventCallback((stepType: AccommodationsTourStepType, stepId: string, skipped?: boolean) => {
    dispatch({ type: 'ADD_STEP', stepType, stepId, skipped });
  });

  const contextValue = useMemo(
    () => ({
      registerStep,
      registeredSteps: state.steps,
      tourName: 'accommodations',
    }),
    [registerStep, state.steps],
  );

  return (
    <AccommodationsTourStepProvider value={contextValue}>
      <AccommodationsTourProviderInner>{props.children}</AccommodationsTourProviderInner>
    </AccommodationsTourStepProvider>
  );
};

AccommodationsTourProvider.displayName = 'AccommodationsTourProvider';
